import type { Account } from '@/interfaces/account.interface';
import { useProductBusinessUnit } from '@/composables/useProductBusinessUnit';
import { useCurrentAccount } from '@/composables/useCurrentAccount';
import { computed } from 'vue';

const MEXICO_COUNTRY_ID = 52;
const MEXICO_COUNTRY_CODES = new Set(['MX', 'MEX']);
const MEXICO_COUNTRY_SLUG = 'mexico';

function isMexicoAccount(account?: Account) {
  const countryCode = account?.country?.code?.toUpperCase();
  const countrySlug = account?.country?.slug?.toLowerCase();

  return (
    account?.country_id === MEXICO_COUNTRY_ID ||
    account?.country?.id === MEXICO_COUNTRY_ID ||
    (countryCode ? MEXICO_COUNTRY_CODES.has(countryCode) : false) ||
    countrySlug === MEXICO_COUNTRY_SLUG
  );
}

function getWhatsappLinkWithPhone(whatsappUrl: string, phone: string) {
  try {
    const url = new URL(whatsappUrl);
    url.searchParams.set('phone', phone);
    return url.toString();
  } catch {
    return whatsappUrl;
  }
}

export const useSupportWhatsappLink = () => {
  const { config } = useProductBusinessUnit();
  const { account } = useCurrentAccount();

  /**
   * Which country the account is in is a property of the account; which number
   * answers there is a property of the product. A product that names no number
   * for the country keeps its own, so it can never inherit another company's
   * support line (FLOK-1646).
   */
  const countryWhatsapp = computed(() =>
    isMexicoAccount(account.value) ? config.value?.supportWhatsappByCountry?.MX : undefined,
  );

  const whatsappLink = computed(() => {
    const productUrl = config.value?.supportWhatsappUrl;
    if (!productUrl) return '';

    const phone = countryWhatsapp.value?.phone;
    return phone ? getWhatsappLinkWithPhone(productUrl, phone) : productUrl;
  });

  const whatsappLabel = computed(
    () => countryWhatsapp.value?.label ?? config.value?.supportWhatsappLabel ?? '',
  );

  return {
    whatsappLink,
    whatsappLabel,
  };
};
